import {useRef} from 'react';

import {isNil} from 'ramda';
import {isFunction, isNumber} from 'ramda-adjunct';

import {TestIdProps, useDidUpdate, useOnMount} from 'shared/web';

import {Alert} from '../../Alert/Alert';
import {useTranslationContext} from '../../TranslationProvider/TranslationContext';
import {NotificationProps} from '../Notification.types';
import {getAutoClose} from '../utils/getAutoClose';
import {getDefaultMessage} from '../utils/getDefaultMessage';

export interface NotificationContainerProps extends TestIdProps {
  notification: NotificationProps;
  onHide(id: string): void;
  autoClose: false | number;
}

export function NotificationContainer(props: NotificationContainerProps) {
  const variant = props.notification.variant;
  const t = useTranslationContext();
  const defaultMessage = getDefaultMessage(variant, t);

  const notificationAutoClose = props.notification.autoClose;
  const autoCloseTimeout = getAutoClose(props.autoClose, notificationAutoClose);
  const hideTimeout = useRef<number>(null);

  const handleHide = () => {
    const notificationId = props.notification.id;
    if (isNil(notificationId)) {
      return;
    }

    props.onHide(notificationId);
    if (hideTimeout.current) {
      window.clearTimeout(hideTimeout.current);
    }
  };

  const cancelDelayedHide = () => {
    if (hideTimeout.current) {
      window.clearTimeout(hideTimeout.current);
    }
  };

  const handleDelayedHide = () => {
    if (isNumber(autoCloseTimeout)) {
      hideTimeout.current = window.setTimeout(handleHide, autoCloseTimeout);
    }
  };

  useOnMount(() => {
    if (isFunction(props.notification.onOpen)) {
      props.notification.onOpen(props.notification);
    }
  });
  useOnMount(handleDelayedHide, cancelDelayedHide);

  useDidUpdate(() => {
    cancelDelayedHide();
    handleDelayedHide();
  }, [props.autoClose, props.notification.autoClose]);

  const alertConfig =
    props.notification.layout === 'detailed'
      ? {
          layout: props.notification.layout,
          type: props.notification.type,
          variant: props.notification.variant,
          title: props.notification.title,
          message: props.notification.message || defaultMessage,
          hyperlinks: props.notification.hyperlinks,
          isLoading: props.notification.isLoading,
          actions: props.notification.actions,
          secondaryMessage: props.notification.secondaryMessage,
        }
      : {
          layout: props.notification.layout,
          type: props.notification.type,
          variant: props.notification.variant,
          title: props.notification.title,
          message: props.notification.message || defaultMessage,
          hyperlinks: props.notification.hyperlinks,
        };

  return (
    <div onMouseEnter={cancelDelayedHide} onMouseLeave={handleDelayedHide}>
      <Alert {...alertConfig} onClose={handleHide} />
    </div>
  );
}
